import moment from "moment";
import { SIGN_NAME_TO_SIGN_NUMBER } from "../utils/enums";

/**
 * Capitalize the first letter of a string
 * @param {string} str - The string to capitalize
 * @returns {string} - The capitalized string
 */
export const capitalizeFirstLetter = (str) => {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

/**
 * Calculate age from birth date
 * @param {string|Date} birthDate - The birth date
 * @returns {number|null} - The calculated age or null if invalid
 */
export const calculateAge = (birthDate) => {
  if (!birthDate) return null;

  const today = moment();
  const birthDateObj = moment(birthDate);

  let age = today.year() - birthDateObj.year();
  const monthDiff = today.month() - birthDateObj.month();

  if (
    monthDiff < 0 ||
    (monthDiff === 0 && today.date() < birthDateObj.date())
  ) {
    age--;
  }

  return age;
};

/**
 * Converts a UTC datetime into local birth datetime based on timezone offset.
 * @param {string} utcDate - UTC date string (e.g., "2001-10-17T17:00:00Z")
 * @param {string} utcOffset - Timezone offset (e.g., "+05:30" or "-07:00")
 * @returns {string|null} Local formatted date (e.g., "17 Oct 2001, 10:30 PM")
 */
export const calculateLocalDOB = (utcDate, utcOffset) => {
  if (!utcDate || !utcOffset) return null;

  try {
    // Parse the UTC date and apply offset
    const localMoment = moment.utc(utcDate).utcOffset(utcOffset);

    // Format the local date and time nicely
    return localMoment.format("D MMM YYYY, h:mm A");
  } catch (error) {
    console.error("Error calculating local DOB:", error);
    return null;
  }
};

/**
 * Format user display name with birth details
 * @param {Object} user - The user object
 * @returns {string} - Formatted user display string
 */
export const formatUserDisplayName = (user) => {
  if (!user) return "";
  
  const userName = capitalizeFirstLetter(user?.name);
  const place = user?.birthDetails?.place?.name;
  const age = calculateAge(user?.birthDetails?.date);
  const dob = calculateLocalDOB(user.birthDetails?.date, user.birthDetails?.utcOffset) || "?";
  
  return `${userName} - ${place} (${age} years, DOB: ${dob})`;
};

export const currentDate = moment();

export const currentYear = currentDate.year();

export const currentMonth = currentDate.month() + 1;

export const currentDay = currentDate.date();

/**
 * Render selected user for dropdown display
 * @param {string} selectedId - The selected user ID
 * @param {Array} users - Array of users
 * @returns {string|JSX.Element} - The rendered user display
 */
export const renderSelectedUser = (selectedId, users) => {
  if (!selectedId) return <em>Select a user</em>;
  const user = users.find((u) => u._id === selectedId);
  if (!user) return null;
  const userName = capitalizeFirstLetter(user.name || user.email);
  return `${userName} - ${user.birthDetails?.place?.name || "Unknown"} (${
    calculateAge(user.birthDetails?.date) || "?"
  } years, DOB: ${
    calculateLocalDOB(
      user.birthDetails?.date,
      user.birthDetails?.utcOffset
    ) || "?"
  })`;
};

export const category = {
  "Yearly Snapshot": "YEARLY_OVERVIEW",
  "Monthly Snapshot": "MONTHLY_OVERVIEW",
  Day: "DAY",
  Notification: "NOTIFICATION",
  Nadi: "NADI",
  "Nadi Degree": "NADI_DEGREE",
};

export const categoryType = {
  Promise: "PROMISE",
  Key: "KEY",
  Career: "CAREER",
  Relationship: "RELATIONSHIP",
  Finance: "FINANCE",
  Health: "HEALTH",
  Legal: "LEGAL",
  Education: "EDUCATION",
  Comfort: "COMFORT",
  Family: "FAMILY",
  Remedy: "REMEDY",
  Caution: "CAUTION",
  Tag: "TAG",

};

export const predictionType = {
  "Current Month": "CURRENT_MONTH",
  January: 1,
  February: 2,
  March: 3,
  April: 4,
  May: 5,
  June: 6,
  July: 7,
  August: 8,
  September: 9,
  October: 10,
  November: 11,
  December: 12,

};

// Generate days for selected month (uses moment)
// export const generateDays = (month = null) => {
//   const moment = require('moment');
  
//   // If no month provided, use current month
//   let targetMonth;
//   if (month === null || month === undefined) {
//     targetMonth = moment().month() + 1; // Convert to 1-based
//   } else if (typeof month === 'number') {
//     targetMonth = month; // Already 1-based (1-12)
//   } else {
//     targetMonth = moment().month() + 1; // Fallback to current month
//   }
  
//   // Get current year (we just need year to calculate days in month)
//   const currentYear = moment().year();
  
//   // Create a moment object for the first day of the target month
//   const targetDate = moment().year(currentYear).month(targetMonth - 1).date(1);
  
//   // Get the number of days in the selected month
//   const daysInMonth = targetDate.daysInMonth();

//   const days = {};
//   for (let i = 1; i <= daysInMonth; i++) {
//     days[i] = i; // Just numbers, not "Day 1"
//   }
//   return days;
// };

export const generateDays = (month = null, year = null) => {
  const moment = require("moment");

  const targetYear = typeof year === "number" ? year : moment().year();

  let targetMonth;
  if (month === null || month === undefined) {
    targetMonth = moment().month() + 1; 
  } else if (typeof month === "number") {
    targetMonth = month; 
  } else {
    targetMonth = moment().month() + 1;
  }
  const targetDate = moment()
    .year(targetYear)
    .month(targetMonth - 1)
    .date(1);
  const daysInMonth = targetDate.daysInMonth();

  const days = {};
  for (let i = 1; i <= daysInMonth; i++) {
    days[i] = i;
  }

  return days;
};


export const monthNames = [
  { value: 1, label: "1" },
  { value: 2, label: "2" },
  { value: 3, label: "3" },
  { value: 4, label: "4" },
  { value: 5, label: "5" },
  { value: 6, label: "6" },
  { value: 7, label: "7" },
  { value: 8, label: "8" },
  { value: 9, label: "9" },
  { value: 10, label: "10" },
  { value: 11, label: "11" },
  { value: 12, label: "12" },
];

export const getZodiacName=(zodiacNumber)=> {
  const zodiacNames = Object.keys(SIGN_NAME_TO_SIGN_NUMBER).reduce((acc, sign) => {
    const number = SIGN_NAME_TO_SIGN_NUMBER[sign];
    acc[number] = sign.charAt(0).toUpperCase() + sign.slice(1).toLowerCase();
    return acc;
  }, {});

  return zodiacNames[zodiacNumber] || "Invalid zodiac number";
}

export const removeFalsyValues = (obj) =>
  Object.fromEntries(
    Object.entries(obj).filter(([_, value]) => Boolean(value))
  );

  export function isValidDateYMD(year, month, day, timezone = "UTC") {
  const m = moment.tz(
    { year, month: month - 1, date : day },
    timezone
  );

  return (
    m.isValid() &&
    m.year() === year &&
    m.month() === month - 1 &&
    m.date() === Number(day)
  );
}